import type React from "react";
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  IconButton,
} from "@mui/material";
import { AddCircleOutlineOutlined } from "@mui/icons-material";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/Index";
import { INetworkInfo } from "../types/view-stream";

interface NetworkingStatisticsCardProps {
  networkData: INetworkInfo[];
  onOpenNetworking: () => void;
}

const NetworkingStatisticsCard: React.FC<NetworkingStatisticsCardProps> = ({
  networkData,
  onOpenNetworking,
}) => {
  const reduxTheme = useSelector((state: RootState) => state.theme);

  const preferredOrder = [
    "Ethernet",
    "WiFi",
    "USB 1",
    "USB 2",
    "USB 3",
    "USB 4",
  ];

  const groupedAndSortedNetworks = () => {
    if (!Array.isArray(networkData)) return [];

    const sorted: INetworkInfo[] = [];
    for (const type of preferredOrder) {
      const items = networkData.filter((n) => n.network_type === type);
      for (let i = 0; i < items.length; i++) {
        const displayType = i === 0 ? type : `${type} (${i + 1})`;
        sorted.push({ ...items[i], display_type: displayType });
      }
    }
    return sorted;
  };

  const sortedNetworks = groupedAndSortedNetworks();

  return (
    <Card sx={{ boxShadow: 0, height: "auto" }}>
      <CardContent>
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Typography variant="h6" gutterBottom fontWeight="bold">
            Networking Statistics
          </Typography>
          <IconButton onClick={onOpenNetworking}>
            <AddCircleOutlineOutlined />
          </IconButton>
        </Box>

        <Grid container spacing={3}>
          {sortedNetworks.length > 0 ? (
            sortedNetworks.map((network: INetworkInfo) => (
              <Grid size={{ xs: 12, sm: 6 }} key={network.id}>
                <Box>
                  <Typography variant="body1" fontWeight="bold" gutterBottom>
                    {network.display_type}
                  </Typography>
                  <Grid container spacing={1} sx={{ textAlign: "left" }}>
                    <Grid size={4}>
                      <Typography
                        variant="body2"
                        sx={{ color: reduxTheme.primary_colors[300] }}
                      >
                        Provider
                      </Typography>
                      <Typography variant="body2">
                        {network.service_provider || "N/A"}
                      </Typography>
                    </Grid>
                    <Grid size={4}>
                      <Typography
                        variant="body2"
                        sx={{ color: reduxTheme.primary_colors[300] }}
                      >
                        Router Type
                      </Typography>
                      <Typography variant="body2">
                        {network.router_type || "N/A"}
                      </Typography>
                    </Grid>
                    <Grid size={4}>
                      <Typography
                        variant="body2"
                        sx={{ color: reduxTheme.primary_colors[300] }}
                      >
                        Speeds
                      </Typography>
                      <Typography variant="h2" fontWeight="bold">
                        {network.speed_mbps}
                        <Typography component="span" variant="body2">
                          Mbps
                        </Typography>
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            ))
          ) : (
            <Grid size={12}>
              <Typography
                variant="body2"
                sx={{
                  color: reduxTheme.primary_colors[300],
                  alignItems: "center",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                No networking data found.
              </Typography>
            </Grid>
          )}
        </Grid>
      </CardContent>
    </Card>
  );
};

export default NetworkingStatisticsCard;
